import {useCallback, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import ConfirmDialog from "@/components/forms/ConfirmDialog.tsx";
import DetailPaneState from "@/components/management/DetailPaneState.tsx";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {useUnsavedChangesGuard} from "@/hooks/useUnsavedChangesGuard.ts";
import SpellForm from "@/spells/SpellForm.tsx";
import {useSaveSpellMutation, useSpellQuery} from "@/spells/SpellQueries.ts";
import {Spell} from "@/spells/model/Spell.ts";

export default function SpellEditor({mode}: {mode: "create" | "edit"}) {
    const {id} = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const {campaignPath} = useCampaign();
    const [dirty, setDirty] = useState(false);
    const query = useSpellQuery(mode === "edit" ? id : undefined);
    const saveMutation = useSaveSpellMutation();
    const {run: guardNavigation, dialogProps} = useUnsavedChangesGuard(dirty, mode === "create" ? {
        title: "Neuen Zauber verwerfen?",
        description: "Die bereits vorgenommenen Eingaben gehen verloren.",
        confirm: "Eingaben verwerfen",
    } : undefined);

    const showSpell = useCallback((saved: Spell) => {
        setDirty(false);
        navigate({pathname: campaignPath(`spells/${saved.id}`), search: location.search}, {replace: true});
    }, [campaignPath, location.search, navigate]);

    const cancel = useCallback(() => guardNavigation(() => {
        setDirty(false);
        const target = mode === "edit" && id ? `spells/${id}` : "spells";
        navigate({pathname: campaignPath(target), search: location.search}, {replace: true});
    }), [campaignPath, guardNavigation, id, location.search, mode, navigate]);

    if (mode === "edit" && query.isPending) return <DetailPaneState title="Zauber wird geladen…" loading/>;
    if (mode === "edit" && query.isError) return <DetailPaneState title="Zauber konnte nicht geladen werden" onRetry={() => void query.refetch()}/>;
    if (mode === "edit" && !query.data) return <DetailPaneState title="Zauber nicht verfügbar"/>;

    return (
        <>
            <title>{mode === "create" ? "Neuer Zauber | DireDawn" : `${query.data!.name} bearbeiten | DireDawn`}</title>
            <ConfirmDialog {...dialogProps}/>
            <SpellForm key={query.data?.id ?? "new"}
                       spell={query.data}
                       onSubmit={saveMutation.mutateAsync}
                       onSaved={showSpell}
                       onCancel={cancel}
                       onDirtyChange={setDirty}/>
        </>
    );
}
